import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { accountService, UserProfile } from '../../../services/accountService';

interface ProfileFormProps {
  userData: UserProfile;
  onSave: (firstName: string, lastName: string) => void;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ userData, onSave }) => {
  const uiData = useSelector((state: RootState) => state.ui);
  const [formData, setFormData] = useState({
    firstName: userData.playerFirstName || '',
    lastName: userData.playerLastName || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Check if changes have been made
  const hasChanges =
    formData.firstName !== userData.playerFirstName ||
    formData.lastName !== userData.playerLastName;

  const canSave = hasChanges && formData.firstName.trim() && formData.lastName.trim() && !loading;

  useEffect(() => {
    setFormData({
      firstName: userData.playerFirstName || '',
      lastName: userData.playerLastName || ''
    });
  }, [userData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSave) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await accountService.updateProfile({
        playerFirstName: formData.firstName.trim(),
        playerLastName: formData.lastName.trim()
      });

      if (result.success) {
        setSuccess(true);
        onSave(formData.firstName.trim(), formData.lastName.trim());
        // Success message will auto-hide after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result.error || 'Failed to update profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '2vh'
    }}>
      {/* First Name */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5vh' }}>
        <label style={{
          fontSize: '1.5vh',
          fontFamily: 'Noto Sans',
          fontWeight: '600',
          color: 'rgba(255, 255, 255, 0.9)'
        }}>
          First Name
        </label>
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          style={{
            backgroundColor: 'rgba(60, 60, 60, 0.8)',
            border: `0.2vh solid rgba(90, 90, 90, 0.8)`,
            padding: '1vh',
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            borderRadius: '0.75vh',
            color: 'rgba(255, 255, 255, 0.9)',
            outline: 'none'
          }}
        />
      </div>

      {/* Last Name */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5vh' }}>
        <label style={{
          fontSize: '1.5vh',
          fontFamily: 'Noto Sans',
          fontWeight: '600',
          color: 'rgba(255, 255, 255, 0.9)'
        }}>
          Last Name
        </label>
        <input
          type="text"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          style={{
            backgroundColor: 'rgba(60, 60, 60, 0.8)',
            border: `0.2vh solid rgba(90, 90, 90, 0.8)`,
            padding: '1vh',
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            borderRadius: '0.75vh',
            color: 'rgba(255, 255, 255, 0.9)',
            outline: 'none'
          }}
        />
      </div>

      {/* Email (Read-only) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5vh' }}>
        <label style={{
          fontSize: '1.5vh',
          fontFamily: 'Noto Sans',
          fontWeight: '600',
          color: 'rgba(255, 255, 255, 0.9)'
        }}>
          Email
        </label>
        <input
          type="email"
          value={userData.email}
          disabled
          style={{
            backgroundColor: 'rgba(40, 40, 40, 0.6)',
            border: `0.2vh solid rgba(60, 60, 60, 0.6)`,
            padding: '1vh',
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            borderRadius: '0.75vh',
            color: 'rgba(255, 255, 255, 0.5)',
            outline: 'none',
            cursor: 'not-allowed'
          }}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: 'rgba(200, 60, 60, 0.2)',
          border: '0.2vh solid rgb(200, 60, 60)',
          padding: '1vh',
          borderRadius: '0.75vh',
          fontSize: '1.5vh',
          fontFamily: 'Noto Sans',
          color: 'rgb(255, 150, 150)',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div style={{
          backgroundColor: 'rgba(60, 200, 60, 0.2)',
          border: '0.2vh solid rgb(60, 200, 60)',
          padding: '1vh',
          borderRadius: '0.75vh',
          fontSize: '1.5vh',
          fontFamily: 'Noto Sans',
          color: 'rgb(150, 255, 150)',
          textAlign: 'center'
        }}>
          Profile updated successfully!
        </div>
      )}

      {/* Save Button */}
      <button
        type="submit"
        disabled={!canSave}
        style={{
          backgroundColor: canSave ? `rgba(${uiData.schemeColor}, 0.2)` : 'rgba(60, 60, 60, 0.8)',
          border: canSave ? `0.2vh solid rgb(${uiData.schemeColor})` : '0.2vh solid rgba(90, 90, 90, 0.8)',
          padding: '1vh',
          fontSize: '1.5vh',
          fontFamily: 'Noto Sans',
          fontWeight: '600',
          borderRadius: '0.75vh',
          cursor: canSave ? 'pointer' : 'not-allowed',
          transition: 'all 0.5s ease',
          color: 'rgba(255, 255, 255, 0.9)'
        }}
      >
        {loading ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
};

export default ProfileForm;
